// frontend/src/UserManagement.tsx - User Management Component for Admins

import Cookies from 'js-cookie';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './apiConfig';

interface User {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface UserFormData {
  username: string;
  email: string;
  full_name: string;
  password: string;
  role: string;
}

interface UserManagementProps {
  onBack: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ onBack }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'EVALUATION_PANEL_MEMBER'
  });

  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    loadUsers();
  }, []);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const token = Cookies.get('access_token');

      const response = await axios.get(`${API_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error: any) {
      showNotification('error', 'Failed to load users');
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.full_name.trim()) {
      errors.full_name = 'Full name is required';
    }

    if (!editingUser && !formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password) {
      // Enhanced password validation to match backend requirements
      if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      } else if (!/[A-Z]/.test(formData.password)) {
        errors.password = 'Password must contain at least one uppercase letter';
      } else if (!/[a-z]/.test(formData.password)) {
        errors.password = 'Password must contain at least one lowercase letter';
      } else if (!/[0-9]/.test(formData.password)) {
        errors.password = 'Password must contain at least one number';
      } else if (!/[^A-Za-z0-9]/.test(formData.password)) {
        errors.password = 'Password must contain at least one symbol (!@#$%^&*)';
      }
    }

    if (!formData.role) {
      errors.role = 'Role is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!validateForm()) {
    return;
  }

  const token = Cookies.get('access_token'); // Get token from cookie

  try {
    if (editingUser) {
      // Update existing user
      const updateData = {
        full_name: formData.full_name,
        email: formData.email,
        role: formData.role,
        is_active: true // You can add a toggle for this
      };

      await axios.put(`${API_URL}/api/auth/users/${editingUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` } // Add token to header
      });
      showNotification('success', 'User updated successfully');
    } else {
      // Create new user
      
      const response = await axios.post(`${API_URL}/api/auth/users`, formData, {
        headers: { Authorization: `Bearer ${token}` } // Add token to header
      });
      showNotification('success', 'User created successfully');
    }

    await loadUsers();
    resetForm();
  } catch (error: any) {
    console.error('User creation/update error:', error);
    console.error('Error response:', error.response);
    console.error('Error data:', error.response?.data);
    
    const errorMessage = error.response?.data?.detail || 'Operation failed';
    showNotification('error', errorMessage);
  }
};

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      full_name: '',
      password: '',
      role: 'EVALUATION_PANEL_MEMBER'
    });
    setFormErrors({});
    setShowCreateForm(false);
    setEditingUser(null);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      password: '',
      role: user.role
    });
    setShowCreateForm(true);
  };

  const handleToggleActive = async (user: User) => {
  try {
    const token = Cookies.get('access_token'); // Get token from cookie
    const updateData = {
      is_active: !user.is_active
    };

    await axios.put(`${API_URL}/api/auth/users/${user.id}`, updateData, {
      headers: { Authorization: `Bearer ${token}` } // Add token to header
    });
    showNotification('success', `User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
    await loadUsers();
  } catch (error: any) {
    showNotification('error', 'Failed to update user status');
  }
};

  const handlePasswordReset = async (user: User) => {
    try {
      const token = Cookies.get('access_token');
      await axios.post(`${API_URL}/api/auth/password-reset-request`, {
        email: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('success', `Password reset email sent to ${user.email}`);
    } catch (error: any) {
      showNotification('error', 'Failed to send password reset email');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'var(--color-error)';
      case 'PROCESS_OWNER':
        return 'var(--color-primary)';
      case 'EVALUATION_PANEL_MEMBER':
        return 'var(--color-success)';
      case 'AUDITOR':
        return 'var(--color-info)';
      default:
        return 'var(--color-text-disabled)';
    }
  };

  const getStatusBadgeColor = (isActive: boolean) => {
    return isActive ? 'var(--color-success)' : 'var(--color-error)';
  };

  const styles = {
    mainContainer: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)'
    },
    contentWrapper: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: 'var(--spacing-xl)'
    },
    pageHeader: {
      textAlign: 'center' as 'center',
      marginBottom: 'var(--spacing-xxl)'
    },
    actionBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 'var(--spacing-xl)',
      flexWrap: 'wrap' as 'wrap',
      gap: 'var(--spacing-md)'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 'var(--spacing-md)',
      marginBottom: 'var(--spacing-xl)'
    },
    statCard: {
      background: 'var(--color-surface)',
      padding: 'var(--spacing-lg)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-sm)',
      border: '1px solid var(--color-surface-variant)',
      textAlign: 'center' as 'center'
    }
};

  if (isLoading) {
    return (
      <div style={styles.mainContainer} className="density-compact">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div className="card" style={{ maxWidth: '400px', margin: 'auto', padding: '3rem' }}>
                <div className="loading-spinner" style={{ margin: '0 auto 1rem auto' }}></div>
                <div style={{ fontSize: '1.2rem', color: 'var(--color-text-primary)' }}>Loading users...</div>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.mainContainer} className="density-compact">
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 1000,
          background: notification.type === 'success' ? 'var(--color-success)' : 'var(--color-error)',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>{notification.type === 'success' ? '✅' : '❌'}</span>
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              style={{ marginLeft: '1rem', background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1.2rem' }}
            >
              &times;
            </button>
          </div>
        </div>
      )}

      <main style={styles.contentWrapper}>
        {/* Page Header */}
        <div style={styles.pageHeader}>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">Manage system users, roles, and access permissions</p>
        </div>

        {/* Quick Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-sm)', color: 'var(--color-primary)' }}>👥</div>
            <div style={{ fontSize: 'var(--font-size-heading-small)', fontWeight: 'var(--font-weight-heading)', color: 'var(--color-text-primary)' }}>
              {users.length}
            </div>
            <div style={{ fontSize: 'var(--font-size-body-small)', color: 'var(--color-text-secondary)' }}>Total Users</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-sm)', color: 'var(--color-success)' }}>✅</div>
            <div style={{ fontSize: 'var(--font-size-heading-small)', fontWeight: 'var(--font-weight-heading)', color: 'var(--color-text-primary)' }}>
              {users.filter(u => u.is_active).length}
            </div>
            <div style={{ fontSize: 'var(--font-size-body-small)', color: 'var(--color-text-secondary)' }}>Active Users</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-sm)', color: 'var(--color-warning)' }}>🔑</div>
            <div style={{ fontSize: 'var(--font-size-heading-small)', fontWeight: 'var(--font-weight-heading)', color: 'var(--color-text-primary)' }}>
              {users.filter(u => u.role === 'ADMIN').length}
            </div>
            <div style={{ fontSize: 'var(--font-size-body-small)', color: 'var(--color-text-secondary)' }}>Administrators</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-sm)', color: 'var(--color-info)' }}>📊</div>
            <div style={{ fontSize: 'var(--font-size-heading-small)', fontWeight: 'var(--font-weight-heading)', color: 'var(--color-text-primary)' }}>
              {users.filter(u => u.role === 'EVALUATION_PANEL_MEMBER').length}
            </div>
            <div style={{ fontSize: 'var(--font-size-body-small)', color: 'var(--color-text-secondary)' }}>Evaluators</div>
          </div>
        </div>
        
        {/* Action Bar */}
        <div style={styles.actionBar}>
          <div>
            <button
              onClick={() => {
                  resetForm();
                  setShowCreateForm(true);
              }}
              className="btn btn-primary"
            >
              + Add New User
            </button>
          </div>
        </div>
        {/* Create/Edit User Form */}
        {showCreateForm && (
          <div className="card" style={{ marginBottom: 'var(--spacing-xl)' }}>
            <div className="card-header">
              <h2 className="card-title">
                {editingUser ? `Edit User: ${editingUser.full_name}` : 'Create New User'}
              </h2>
              <button
                onClick={resetForm}
                className="btn btn-ghost btn-sm"
                style={{ padding: 'var(--spacing-xs)' }}
              >
                ✕
              </button>
            </div>
            <div className="card-content">

              <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                <div className="form-group">
                  <label htmlFor="username" className="form-label required">Username</label>
                  <input 
                    id="username" 
                    type="text" 
                    value={formData.username} 
                    onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value.toLowerCase() }))} 
                    disabled={!!editingUser} 
                    className={`input ${formErrors.username ? 'error' : ''}`}
                    style={{
                      ...(editingUser && {background: 'var(--color-surface-variant)', cursor: 'not-allowed'})
                    }} 
                    placeholder="e.g., john.smith" 
                  />
                  {formErrors.username && <span className="form-error">{formErrors.username}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label required">Email</label>
                  <input 
                    id="email" 
                    type="email" 
                    value={formData.email} 
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} 
                    className={`input ${formErrors.email ? 'error' : ''}`}
                    placeholder="john.smith@company.com" 
                  />
                  {formErrors.email && <span className="form-error">{formErrors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="full_name" className="form-label required">Full Name</label>
                  <input 
                    id="full_name" 
                    type="text" 
                    value={formData.full_name} 
                    onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))} 
                    className={`input ${formErrors.full_name ? 'error' : ''}`}
                    placeholder="John Smith" 
                  />
                  {formErrors.full_name && <span className="form-error">{formErrors.full_name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="role" className="form-label required">Role</label>
                  <select 
                    id="role" 
                    value={formData.role} 
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))} 
                    className={`select ${formErrors.role ? 'error' : ''}`}
                  >
                    <option value="EVALUATION_PANEL_MEMBER">Evaluation Panel Member</option>
                    <option value="PROCESS_OWNER">Process Owner</option>
                    <option value="AUDITOR">Auditor/Probity Advisor</option>
                    <option value="ADMIN">Administrator</option>
                  </select>
                  {formErrors.role && <span className="form-error">{formErrors.role}</span>}
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="password" className={`form-label ${!editingUser ? 'required' : ''}`}>
                    Password {editingUser ? '(leave blank to keep current)' : ''}
                  </label>
                  <input 
                    id="password" 
                    type="password" 
                    value={formData.password} 
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} 
                    className={`input ${formErrors.password ? 'error' : ''}`}
                    style={{ maxWidth: '400px' }}
                    placeholder={editingUser ? 'Enter new password (optional)' : '8+ chars with uppercase, lowercase, numbers, symbols'} 
                  />
                  {!editingUser && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
                      Must be at least 8 characters with uppercase, lowercase, numbers, and symbols (!@#$%^&*)
                    </div>
                  )}
                  {formErrors.password && <span className="form-error">{formErrors.password}</span>}
                </div>

                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'flex-end', marginTop: 'var(--spacing-lg)' }}>
                  <button 
                    type="button" 
                    onClick={resetForm} 
                    className="btn btn-tertiary"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                  >
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Users List */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">System Users</h3>
            <p className="card-subtitle">Manage user accounts, roles, and access permissions</p>
          </div>
          <div className="card-content">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'center' }}>Last Login</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div style={{ fontWeight: 'bold', color: 'var(--color-text-primary)' }}>{user.full_name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>@{user.username} | {user.email}</div>
                      </td>
                      <td>
                        <span 
                          className="badge"
                          style={{ background: getRoleBadgeColor(user.role), color: 'white' }}
                        >
                          {user.role}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span 
                          className="badge"
                          style={{ background: getStatusBadgeColor(user.is_active), color: 'white' }}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                        {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('en-AU') : 'Never'}
                      </td>
                      <td className="table-actions">
                        <div style={{ display: 'flex', gap: 'var(--spacing-xs)', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button 
                            onClick={() => handleEdit(user)} 
                            className="btn btn-secondary btn-sm"
                            title="Edit user details"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => handlePasswordReset(user)} 
                            className="btn btn-warning btn-sm"
                            title="Send password reset email"
                          >
                            Reset
                          </button>
                          {user.id !== currentUser?.id && (
                            <button 
                              onClick={() => handleToggleActive(user)} 
                              className={`btn btn-sm ${user.is_active ? 'btn-destructive' : 'btn-success'}`}
                              title={user.is_active ? 'Deactivate user' : 'Activate user'}
                            >
                              {user.is_active ? 'Disable' : 'Enable'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {users.length === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">👥</div>
                <h3 className="empty-state-title">No Users Found</h3>
                <p className="empty-state-description">Click "Add New User" to create the first user account.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserManagement;